import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { adminFetch, ApiClientError } from "../lib/apiClient";
import { QuestionList } from "../components/admin/QuestionList";
import { QuestionDetail } from "../components/admin/QuestionDetail";
import { AdminNav } from "../components/admin/AdminNav";
import type { AdminQuestion, AdminWebinar, QuestionStatus } from "../types";

interface Kpi {
  pending: number;
  displaying: number;
  answered: number;
  consultation: number;
}

interface QuestionsResponse {
  questions: AdminQuestion[];
  kpi: Kpi;
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "すべて" },
  { value: "pending", label: "未確認" },
  { value: "approved", label: "承認済み" },
  { value: "displaying", label: "表示中" },
  { value: "answered", label: "回答済み" },
  { value: "hidden", label: "非表示" },
];

export function AdminQuestionsPage() {
  const { webinarId } = useParams<{ webinarId: string }>();
  const [searchParams] = useSearchParams();
  const registrationFilter = searchParams.get("registration");
  const questionParam = searchParams.get("question");
  const [questions, setQuestions] = useState<AdminQuestion[]>([]);
  const [kpi, setKpi] = useState<Kpi | null>(null);
  const [webinarTitle, setWebinarTitle] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [consultationOnly, setConsultationOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(questionParam);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!webinarId) return;
    const params = new URLSearchParams();
    params.set("status", statusFilter);
    if (consultationOnly) params.set("consultation", "true");
    if (search.trim()) params.set("q", search.trim());
    if (registrationFilter) params.set("registration", registrationFilter);
    try {
      const data = await adminFetch<QuestionsResponse>(
        `/api/admin/webinars/${webinarId}/questions?${params.toString()}`,
      );
      setQuestions(data.questions);
      setKpi(data.kpi);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "読み込みに失敗しました");
    }
  }, [webinarId, statusFilter, consultationOnly, search, registrationFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Light polling so new questions surface without manual refresh.
  useEffect(() => {
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    adminFetch<{ webinars: AdminWebinar[] }>("/api/admin/webinars")
      .then((d) => {
        const w = d.webinars.find((x) => x.id === webinarId);
        if (w) setWebinarTitle(w.title);
      })
      .catch(() => {});
  }, [webinarId]);

  const selected = useMemo(
    () => questions.find((q) => q.id === selectedId) ?? null,
    [questions, selectedId],
  );

  async function runAction(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "操作に失敗しました");
    } finally {
      setBusy(false);
    }
  }

  function patchStatus(id: string, status: QuestionStatus) {
    return adminFetch(`/api/admin/questions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  }

  const handlers = selected && {
    onSaveEdits: (fields: {
      display_text: string;
      display_name: string;
      operator_memo: string;
    }) =>
      runAction(() =>
        adminFetch(`/api/admin/questions/${selected.id}`, {
          method: "PATCH",
          body: JSON.stringify(fields),
        }).then(() => {}),
      ),
    onApprove: () => runAction(() => patchStatus(selected.id, "approved").then(() => {})),
    onDisplay: () =>
      runAction(() =>
        adminFetch(`/api/admin/webinars/${webinarId}/display-question`, {
          method: "POST",
          body: JSON.stringify({ question_id: selected.id }),
        }).then(() => {}),
      ),
    onAnswered: () =>
      runAction(() =>
        adminFetch(`/api/admin/questions/${selected.id}/mark-answered`, {
          method: "POST",
        }).then(() => {}),
      ),
    onHide: () =>
      runAction(() =>
        adminFetch(`/api/admin/questions/${selected.id}/hide`, {
          method: "POST",
        }).then(() => {}),
      ),
    onResync: () =>
      runAction(() =>
        adminFetch(`/api/admin/questions/${selected.id}/resync-hubspot`, {
          method: "POST",
        }).then(() => {}),
      ),
  };

  return (
    <div className="admin-page">
      <AdminNav webinarId={webinarId} current="questions" />
      <header className="admin-header">
        <h1>質問管理</h1>
        <p className="muted">Webinar: {webinarTitle || webinarId}</p>
        {registrationFilter && (
          <p className="muted">
            特定リードの質問で絞り込み中{" "}
            <a href={`/admin/webinars/${webinarId}/questions`}>（解除）</a>
          </p>
        )}
      </header>

      {kpi && (
        <div className="kpi-bar">
          <span>未確認: {kpi.pending}</span>
          <span>表示中: {kpi.displaying}</span>
          <span>回答済み: {kpi.answered}</span>
          <span>個別相談希望: {kpi.consultation}</span>
        </div>
      )}

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      <div className="admin-body">
        <div className="admin-left">
          <div className="filters">
            <label>
              ステータス
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={consultationOnly}
                onChange={(e) => setConsultationOnly(e.target.checked)}
              />
              相談希望のみ
            </label>
            <input
              type="search"
              placeholder="本文を検索"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <QuestionList
            questions={questions}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </div>

        <div className="admin-right">
          {selected && handlers ? (
            <QuestionDetail question={selected} busy={busy} handlers={handlers} />
          ) : (
            <p className="empty">左の一覧から質問を選択してください</p>
          )}
        </div>
      </div>
    </div>
  );
}
